import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, AlertCircle, AlertTriangle, Info, XCircle, Loader, Download } from 'lucide-react';
import { OrderTimelineLogEntryDto } from '../../../../../types/orderTimeline';
import orderTimelineService from '../../../../../services/orderTimelineService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface TimelineLogEntriesProps {
  orderNo: number;
}

type TFunc = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const getLevelStyle = (level: string | undefined, t: TFunc) => {
  switch (level?.toLowerCase()) {
    case 'information': return { icon: Info, bg: 'bg-blue-50', text: 'text-blue-600', label: t('manager.modules.orderTimeline.logs.levelInfo', {}, 'Info') };
    case 'warning': return { icon: AlertTriangle, bg: 'bg-yellow-50', text: 'text-yellow-600', label: t('manager.modules.orderTimeline.logs.levelWarning', {}, 'Warning') };
    case 'error': return { icon: AlertCircle, bg: 'bg-red-50', text: 'text-red-600', label: t('manager.modules.orderTimeline.logs.levelError', {}, 'Error') };
    case 'fatal': return { icon: XCircle, bg: 'bg-red-100', text: 'text-red-800', label: t('manager.modules.orderTimeline.logs.levelFatal', {}, 'Fatal') };
    default: return { icon: FileText, bg: 'bg-gray-50', text: 'text-gray-600', label: level || '?' };
  }
};

export const TimelineLogEntries: React.FC<TimelineLogEntriesProps> = ({ orderNo }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [logEntries, setLogEntries] = useState<OrderTimelineLogEntryDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const entries = await orderTimelineService.getOrderTimelineLogs(orderNo);
      setLogEntries(entries);
    } catch (err: any) {
      setError(err.message || t('manager.modules.orderTimeline.logs.loadFailed', {}, 'Kunne ikke hente loggdata'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    // Load logs on first expand
    if (willExpand && logEntries === null && !loading) {
      loadLogs();
    }
  };

  return (
    <div className="mb-4">
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <FileText className="w-4 h-4" />
        {t('manager.modules.orderTimeline.logs.title', {}, 'Applikasjonslogger')}
        {logEntries !== null && ` (${logEntries.length})`}
        {logEntries === null && (
          <span className="text-[10px] font-normal text-gray-400 ml-1">{t('manager.modules.orderTimeline.logs.clickToLoad', {}, '— klikk for å hente')}</span>
        )}
      </button>

      {expanded && (
        <div className="space-y-1.5">
          {loading && (
            <div className="flex items-center justify-center py-6 bg-white rounded-lg border border-gray-200">
              <Loader className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="ml-2 text-sm text-gray-500">{t('manager.modules.orderTimeline.logs.loading', {}, 'Henter loggdata...')}</span>
            </div>
          )}

          {error && (
            <div className="text-center py-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={loadLogs}
                className="mt-2 flex items-center gap-1 mx-auto px-3 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg"
              >
                <Download className="w-3 h-3" />
                {t('manager.modules.orderTimeline.retry', {}, 'Prøv igjen')}
              </button>
            </div>
          )}

          {!loading && !error && logEntries !== null && logEntries.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-4 bg-white rounded-lg border border-gray-200">
              {t('manager.modules.orderTimeline.logs.empty', {}, 'Ingen applikasjonslogger funnet for denne ordren')}
            </div>
          )}

          {!loading && !error && logEntries && logEntries.length > 0 &&
            logEntries.map((entry) => {
              const style = getLevelStyle(entry.level, t);
              const Icon = style.icon;
              return (
                <div key={entry.id} className={`${style.bg} border border-gray-200 rounded-lg p-2.5`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.text}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 break-words">
                        {entry.message || '–'}
                      </div>
                      {entry.exception && (
                        <div className="text-xs text-red-600 mt-1 bg-red-50 p-1.5 rounded font-mono whitespace-pre-wrap max-h-24 overflow-y-auto">
                          {entry.exception}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400 flex-wrap">
                        <span>{formatDateTime(entry.timeStamp)}</span>
                        {entry.operatorId && (
                          <span>{t('warehouse.general.operator', {}, 'Operatør')}: {entry.operatorName || entry.operatorId}</span>
                        )}
                        {entry.requestPath && (
                          <span className="font-mono">
                            {entry.requestMethod} {entry.requestPath}
                          </span>
                        )}
                        <span className={`px-1 py-0 rounded ${style.text} bg-white/50`}>{style.label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      )}
    </div>
  );
};
